import { c as _c } from "react-compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import { Metrics } from 'collaboration-sidebar/utils/metrics';
import ErrorBoundary from './ErrorBoundary';
import { HAS_UNRECOVERABLE_ERROR } from '../../constants/CollaborationSidebarStateProperties';
import debounce from '../../utils/debounce';
import { jsx as _jsx } from "react/jsx-runtime";
const TIME_TO_RESET = 3000;
function useRenderCount() {
  'use no memo';

  const renderCount = useRef(0);
  const debouncedReset = useMemo(() => debounce(() => {
    renderCount.current = 0;
  }, TIME_TO_RESET), []);
  useEffect(() => {
    renderCount.current += 1;
    debouncedReset();
  });

  // React compiler does not like accessing the ref during render
  return renderCount.current;
}

/**
 * From: https://git.hubteam.com/HubSpot/Critsit/issues/4039
 * This is a somehow hacky approach but could be useful for us to remediate errors when ErrorBoundary is not enough.
 *
 * This component will render its children until a certain threshold and then it'll stop rendering and notify us in Grafana.
 */
export default function ErrorCountWrapper(t0) {
  "use memo";

  const $ = _c(10);
  const {
    appName,
    children,
    errorBoundaryFingerprint,
    threshold,
    onUpdateCollaborationSidebarState
  } = t0;
  const renderCount = useRenderCount();
  const hasRenderedTooMuch = renderCount > threshold;
  let t1;
  let t2;
  if ($[0] !== appName || $[1] !== errorBoundaryFingerprint || $[2] !== hasRenderedTooMuch || $[3] !== onUpdateCollaborationSidebarState) {
    t1 = () => {
      if (!hasRenderedTooMuch) {
        return;
      }
      onUpdateCollaborationSidebarState({
        [HAS_UNRECOVERABLE_ERROR]: true
      });
      Metrics.counter("infinite-loop", {
        app_name: appName
      }).increment();
      Raven.captureException(new Error(`[${appName}] ${errorBoundaryFingerprint} rendered too many times`), {
        extra: {
          appName
        },
        fingerprint: [errorBoundaryFingerprint],
        tags: {
          lib: "collaboration-sidebar"
        }
      });
    };
    t2 = [appName, errorBoundaryFingerprint, hasRenderedTooMuch, onUpdateCollaborationSidebarState];
    $[0] = appName;
    $[1] = errorBoundaryFingerprint;
    $[2] = hasRenderedTooMuch;
    $[3] = onUpdateCollaborationSidebarState;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  if (hasRenderedTooMuch) {
    return null;
  }
  let t3;
  if ($[6] !== children || $[7] !== errorBoundaryFingerprint || $[8] !== onUpdateCollaborationSidebarState) {
    t3 = /*#__PURE__*/_jsx(ErrorBoundary, {
      fingerprint: errorBoundaryFingerprint,
      onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
      renderFallback: _temp,
      children: children
    });
    $[6] = children;
    $[7] = errorBoundaryFingerprint;
    $[8] = onUpdateCollaborationSidebarState;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp() {
  return null;
}